import React, { useState, useMemo, useEffect } from 'react';
import { 
  SkillNode, 
  GLOBAL_SKILL_TAXONOMY, 
  flattenSkillTaxonomy,
  findSkillById
} from '../../data/skillTaxonomy';
import { 
  TimeCommitmentNode, 
  TIME_COMMITMENT_TAXONOMY 
} from '../../data/timeCommitmentTaxonomy';
import { 
  WorkEnvironmentNode, 
  WORK_ENVIRONMENT_TAXONOMY 
} from '../../data/workEnvironmentTaxonomy';
import { SkillLevel, SkillWithLevel, MarketScope } from '../../services/businessIdeaTypes';
import SkillRecommendations from './SkillRecommendations';

interface WizardProps {
  onComplete: (data: {
    skills: SkillWithLevel[];
    timeCommitment: TimeCommitmentNode;
    workEnvironment: WorkEnvironmentNode[];
    marketScope: MarketScope;
  }) => void;
  onCancel?: () => void;  
  prefill?: {
    skills?: Array<{ id: string; level?: SkillLevel } | string>;
    timeCommitmentId?: string;
    workEnvironmentIds?: string[];
    marketScope?: MarketScope;
  };
}

// Extended skill type to include top-level information
interface ExtendedSkill {
  id: string;
  name: string;
  fullPath: string[];
  isTopLevel?: boolean;
  originalNode?: SkillNode;
}

const IdeaGeneratorWizard: React.FC<WizardProps> = ({ onComplete, onCancel, prefill }) => {
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<SkillWithLevel[]>([]);
  const [expandedSkills, setExpandedSkills] = useState<string[]>([]);
  const [timeCommitment, setTimeCommitment] = useState<TimeCommitmentNode | null>(null);
  const [workEnvironment, setWorkEnvironment] = useState<WorkEnvironmentNode[]>([]);
  const [marketScope, setMarketScope] = useState<MarketScope>('local');
  const [skillBeingAdded, setSkillBeingAdded] = useState<{ skill: SkillNode | ExtendedSkill; showLevels: boolean } | null>(null);

  // Local GA4 helper
  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      if (w && typeof w.gtag === 'function') {
        w.gtag('event', event, params);
      }
    } catch {}
    // console.log(`[wizard-analytics] ${event}`, params);
  };

  // Fire initial wizard view
  useEffect(() => {
    track('view_idea_wizard');
  }, []);

  // Track step views
  useEffect(() => {
    track('wizard_step_view', { step });
  }, [step]);

  // Apply prefill from parent (e.g., URL query params)
  useEffect(() => {
    if (!prefill) return;

    try {
      if (prefill.skills && prefill.skills.length > 0) {
        const flattened = flattenSkillTaxonomy(GLOBAL_SKILL_TAXONOMY);
        const mapped: SkillWithLevel[] = prefill.skills
          .map((s) => (typeof s === 'string' ? { id: s } : s))
          .map((s) => {
            const node = flattened.find(f => f.id === s.id) || findSkillById(s.id);
            if (!node) return null;
            return {
              id: node.id,
              name: node.name,
              level: (s as { level?: SkillLevel }).level || 'Intermediate'
            } as SkillWithLevel;
          })
          .filter(Boolean) as SkillWithLevel[];
        if (mapped.length > 0) setSelectedSkills(mapped);
      }

      if (prefill.timeCommitmentId) {
        const t = TIME_COMMITMENT_TAXONOMY.find(tc => tc.id === prefill.timeCommitmentId || tc.name.toLowerCase() === prefill.timeCommitmentId?.toLowerCase());
        if (t) setTimeCommitment(t);
      }

      if (prefill.workEnvironmentIds && prefill.workEnvironmentIds.length > 0) {
        const mappedEnv = WORK_ENVIRONMENT_TAXONOMY.filter(w => prefill.workEnvironmentIds!.includes(w.id) || prefill.workEnvironmentIds!.includes(w.name.toLowerCase()));
        if (mappedEnv.length > 0) setWorkEnvironment(mappedEnv);
      }

      if (prefill.marketScope) {
        setMarketScope(prefill.marketScope);
      }
    } catch (e) {
      // swallow silently; prefill is optional
    }
  }, [prefill]);

  // Flatten all skills for comprehensive search
  const allSkills = useMemo(() => {
    // First, get the top-level skills directly from GLOBAL_SKILL_TAXONOMY
    const topLevelSkills: ExtendedSkill[] = GLOBAL_SKILL_TAXONOMY.children?.map(skill => ({
      id: skill.id,
      name: skill.name,
      fullPath: [skill.name],
      isTopLevel: true,
      originalNode: skill
    })) || [];

    return topLevelSkills;
  }, []);

  // Filter skills based on search query
  const filteredSkills = useMemo(() => {
    if (!searchQuery) return allSkills;
    const query = searchQuery.toLowerCase();
    
    // Search through all skills in the taxonomy
    const searchResults = flattenSkillTaxonomy(GLOBAL_SKILL_TAXONOMY)
      .filter(skill => 
        skill.name.toLowerCase().includes(query) ||
        skill.fullPath?.some(path => path.toLowerCase().includes(query))
      )
      .map(skill => {
        const foundSkill = findSkillById(skill.id);
        return {
          id: skill.id,
          name: skill.name,
          fullPath: skill.fullPath || [],
          isTopLevel: GLOBAL_SKILL_TAXONOMY.children?.some(top => top.id === skill.id),
          originalNode: foundSkill || undefined
        } as ExtendedSkill;
      });

    return searchResults;
  }, [searchQuery, allSkills]);

  // Skill selection and management
  const handleInitiateSkillAdd = (skill: SkillNode | ExtendedSkill) => {
    setSkillBeingAdded({ skill, showLevels: true });
  };

  const handleAddSkillWithLevel = (level: SkillLevel) => {
    if (skillBeingAdded) {
      const skill = skillBeingAdded.skill;
      // Normalize the skill object
      const normalizedSkill: SkillNode = 'originalNode' in skill 
        ? skill.originalNode || { id: skill.id, name: skill.name }
        : skill;

      // Prevent duplicate skills
      if (selectedSkills.some(s => s.id === normalizedSkill.id)) {
        console.log('Skill already exists, skipping:', normalizedSkill.id);
        return;
      }

      setSelectedSkills(prev => {
        const newSkills = [...prev, {
          id: normalizedSkill.id,
          name: normalizedSkill.name,
          level
        }];
        
        console.log('Updated selected skills:', newSkills);
        // Track add skill action
        track('select_skill', {
          skill_id: normalizedSkill.id,
          skill_name: normalizedSkill.name,
          level
        });
        return newSkills;
      });

      // Reset the skill being added
      setSkillBeingAdded(null);
    }
  };

  const handleRemoveSkill = (skillId: string) => {
    const removed = selectedSkills.find(s => s.id === skillId);
    setSelectedSkills(prev => prev.filter(s => s.id !== skillId));
    if (removed) {
      track('remove_skill', {
        skill_id: removed.id,
        skill_name: removed.name,
        level: removed.level
      });
    }
  };

  // Render skill level selection dropdown
  const renderSkillLevelDropdown = () => {
    if (!skillBeingAdded) return null;

    const levels: SkillLevel[] = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
    
    return (
      <div className="absolute z-50 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
        <div className="py-1" role="menu" aria-orientation="vertical">
          {levels.map((level) => (
            <button
              key={level}
              onClick={() => handleAddSkillWithLevel(level)}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              role="menuitem"
            >
              {level}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Skill tree expansion
  const toggleSkillExpansion = (skillId: string) => {
    setExpandedSkills(prev => 
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleExpandSkill = (skillId: string) => {
    toggleSkillExpansion(skillId);
  };

  // Render skill tree with expandable categories
  const renderSkillTree = (skills: ExtendedSkill[]) => {
    return skills.map((skill) => (
      <div 
        key={skill.id}
        className={`
          relative p-4 rounded-lg transition-all duration-200
          ${skill.isTopLevel ? 'bg-white shadow-sm hover:shadow-md' : 'hover:bg-gray-50'}
        `}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex-grow">
            <div className="flex items-start justify-between">
              <h4 className="font-medium text-gray-900">{skill.name}</h4>
            </div>
            {skill.originalNode?.description && (
              <p className="mt-1 text-sm text-gray-500">{skill.originalNode.description}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {skill.originalNode?.children && skill.originalNode.children.length > 0 && (
              <button
                onClick={() => toggleSkillExpansion(skill.id)}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors flex items-center gap-1"
              >
                <span>Subcategories</span>
                <svg 
                  className={`w-4 h-4 transform transition-transform ${expandedSkills.includes(skill.id) ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
            
            {(!skill.originalNode?.children || skill.originalNode.children.length === 0) && (
              <div className="relative">
                <button
                  onClick={() => handleInitiateSkillAdd(skill.originalNode!)}
                  className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                >
                  Add Skill
                </button>
                {skillBeingAdded?.skill.id === skill.originalNode?.id && renderSkillLevelDropdown()}
              </div>
            )}
          </div>
        </div>

        {/* Subcategories */}
        {expandedSkills.includes(skill.id) && skill.originalNode?.children && (
          <div className="mt-4 pl-4 border-l-2 border-gray-100 space-y-4">
            {skill.originalNode.children.map(childSkill => (
              <div 
                key={childSkill.id}
                className="relative"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-grow">
                    <h5 className="font-medium text-gray-800">{childSkill.name}</h5>
                    {childSkill.description && (
                      <p className="mt-1 text-sm text-gray-500">{childSkill.description}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {childSkill.children && childSkill.children.length > 0 ? (
                      <button
                        onClick={() => toggleSkillExpansion(childSkill.id)}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors flex items-center gap-1"
                      >
                        <span>Subcategories</span>
                        <svg 
                          className={`w-4 h-4 transform transition-transform ${expandedSkills.includes(childSkill.id) ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    ) : (
                      <div className="relative">
                        <button
                          onClick={() => handleInitiateSkillAdd(childSkill)}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                        >
                          Add Skill
                        </button>
                        {skillBeingAdded?.skill.id === childSkill.id && renderSkillLevelDropdown()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Nested subcategories */}
                {expandedSkills.includes(childSkill.id) && childSkill.children && (
                  <div className="mt-4 pl-4 border-l-2 border-gray-100 space-y-4">
                    {childSkill.children.map(grandChild => (
                      <div 
                        key={grandChild.id} 
                        className="flex items-center justify-between gap-4"
                      >
                        <div className="flex-grow">
                          <h6 className="font-medium text-gray-800">{grandChild.name}</h6>
                          {grandChild.description && (
                            <p className="mt-1 text-sm text-gray-500">{grandChild.description}</p>
                          )}
                        </div>
                        <div className="relative">
                          <button
                            onClick={() => handleInitiateSkillAdd(grandChild)}
                            className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                          >
                            Add Skill
                          </button>
                          {skillBeingAdded?.skill.id === grandChild.id && renderSkillLevelDropdown()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ));
  };

  // New function to generate context-aware skill recommendations
  const generateSkillRecommendations = () => {
    if (selectedSkills.length === 0) return [];

    // Get the last selected skill for context
    const lastSkill = selectedSkills[selectedSkills.length - 1];
    const fullSkillNode = findSkillById(lastSkill.id);

    // If no skill node found, return empty recommendations
    if (!fullSkillNode) return [];

    // Generate recommendations based on skill context
    const recommendations: Array<{
      skill: string;
      relationship: 'complementary' | 'prerequisite' | 'natural_progression';
      relevance: number;
      description: string;
      source: string;
    }> = [];

    // Add complementary skills
    if (fullSkillNode.complementarySkills) {
      recommendations.push(
        ...fullSkillNode.complementarySkills.map(skill => ({
          skill: skill.name,
          relationship: 'complementary' as const,
          relevance: 0.8,
          description: `Pairs well with ${lastSkill.name} for enhanced capabilities`,
          source: 'Skill Synergy Analysis'
        }))
      );
    }

    // Add prerequisite skills
    if (fullSkillNode.prerequisiteSkills) {
      recommendations.push(
        ...fullSkillNode.prerequisiteSkills.map(skill => ({
          skill: skill.name,
          relationship: 'prerequisite' as const,
          relevance: 0.9,
          description: `Foundation skill that enhances ${lastSkill.name}`,
          source: 'Skill Progression Path'
        }))
      );
    }

    // Add natural progression skills (from parent category)
    const parentSkill = findParentSkill(lastSkill.id);
    if (parentSkill && parentSkill.children) {
      const siblingSkills = parentSkill.children
        .filter(child => child.id !== lastSkill.id)
        .map(skill => ({
          skill: skill.name,
          relationship: 'natural_progression' as const,
          relevance: 0.7,
          description: `Related skill in the ${parentSkill.name} category`,
          source: 'Category Progression'
        }));
      recommendations.push(...siblingSkills);
    }

    // Ensure unique recommendations and limit to 6
    return Array.from(new Set(recommendations.map(r => r.skill)))
      .map(skill => recommendations.find(r => r.skill === skill)!)
      .slice(0, 6);
  };

  // Helper function to find parent skill
  const findParentSkill = (skillId: string, node: SkillNode = GLOBAL_SKILL_TAXONOMY): SkillNode | null => {
    if (!node.children) return null;
    if (node.children.some(child => child.id === skillId)) return node;
    for (const child of node.children) {
      const result = findParentSkill(skillId, child);
      if (result) return result;
    }
    return null;
  };

  // Market scope options
  const MARKET_SCOPE_OPTIONS: Array<{
    id: MarketScope;
    name: string;
    description: string;
    pros: string[];
  }> = [
    {
      id: 'local',
      name: 'Local',
      description: 'Focus on your immediate community or city',
      pros: ['Lower competition', 'Easier to establish', 'Personal connections']
    },
    {
      id: 'national',
      name: 'National',
      description: 'Expand across the entire country',
      pros: ['Larger market', 'More growth potential', 'Broader customer base']
    },
    {
      id: 'international',
      name: 'International',
      description: 'Global market with worldwide opportunities',
      pros: ['Unlimited scaling', 'Diverse revenue streams', 'High innovation potential']
    }
  ];

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">Select Your Skills</h2>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Skill Categories</h3>
                <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-sm p-4">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
                  <div className="relative z-10">
                    {filteredSkills.length > 0 ? (
                      <div className="grid gap-4">
                        {renderSkillTree(filteredSkills)}
                      </div>
                    ) : (
                      <p className="text-gray-500 bg-white/50 backdrop-blur-sm p-3 rounded-lg">No skills found matching your search.</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <div className="sticky top-4">
                  <h3 className="text-lg font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Selected Skills</h3>
                  <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-sm p-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-blue-500 opacity-5"></div>
                    <div className="relative z-10">
                      {selectedSkills.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {selectedSkills.map((skill) => (
                            <div 
                              key={skill.id} 
                              className="bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center shadow-sm border border-white/50 transition-all duration-300 hover:shadow"
                            >
                              <span className="text-gray-800">{skill.name}</span>
                              <span className="text-blue-600 text-sm ml-1 font-medium">({skill.level})</span>
                              <button 
                                onClick={() => handleRemoveSkill(skill.id)}
                                className="ml-2 text-gray-400 hover:text-red-500 transition-colors duration-300"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 bg-white/50 backdrop-blur-sm p-3 rounded-lg">No skills selected yet.</p>
                      )}
                    </div>
                  </div>

                  {selectedSkills.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Recommended Skills</h3>
                      <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-sm p-4">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-blue-500 opacity-5"></div>
                        <div className="relative z-10">
                          <SkillRecommendations 
                            recommendations={generateSkillRecommendations()}
                            onAddSkill={(skillData) => {
                              console.log('Adding recommended skill:', skillData);
                              const foundSkill = findSkillById(skillData.id);
                              if (foundSkill) {
                                handleInitiateSkillAdd(foundSkill);
                              } else {
                                // If skill not found in taxonomy, create a new one
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const newSkill = {
                                  id: skillData.id,
                                  name: skillData.name,
                                };
                                setSelectedSkills(prev => [...prev, {
                                  id: skillData.id,
                                  name: skillData.name,
                                  level: skillData.level
                                }]);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">Time Commitment</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {TIME_COMMITMENT_TAXONOMY.map(option => (
                <button
                  key={option.id}
                  onClick={() => {
                    setTimeCommitment(option);
                    track('set_time_commitment', { id: option.id, name: option.name });
                  }}
                  className={`
                    relative overflow-hidden rounded-xl shadow-sm border p-4 text-left transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow
                    ${timeCommitment?.id === option.id 
                      ? 'bg-blue-50/70 backdrop-blur-sm border-blue-300/50 ring-2 ring-blue-500/70' 
                      : 'bg-white/30 backdrop-blur-sm border-white/40 hover:bg-blue-50/50'
                    }
                  `}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${timeCommitment?.id === option.id ? 'from-blue-400/10 to-indigo-400/10' : 'from-gray-200/5 to-gray-100/5'}`}></div>
                  <div className="relative z-10">
                    <h3 className={`font-semibold ${timeCommitment?.id === option.id ? 'text-blue-700' : 'text-gray-800'}`}>{option.name}</h3>
                    <p className="text-sm text-gray-600 mt-2">{option.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">Work Environment</h2>
              <p className="text-gray-700 mb-6">
                Select the work environment that suits your business idea.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {WORK_ENVIRONMENT_TAXONOMY.map(option => (
                  <div
                    key={option.id}
                    onClick={() => {
                      const isSelected = workEnvironment.some(env => env.id === option.id);
                      setWorkEnvironment(prev => 
                        isSelected 
                          ? prev.filter(env => env.id !== option.id)
                          : [...prev, option]
                      );
                      track('toggle_work_environment', {
                        id: option.id,
                        name: option.name,
                        selected: !isSelected
                      });
                    }}
                    className={`
                      relative overflow-hidden rounded-xl shadow-sm border p-4 text-left transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow cursor-pointer
                      ${workEnvironment.some(env => env.id === option.id)
                        ? 'bg-blue-50/70 backdrop-blur-sm border-blue-300/50 ring-2 ring-blue-500/70' 
                        : 'bg-white/30 backdrop-blur-sm border-white/40 hover:bg-blue-50/50'
                      }
                    `}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${workEnvironment.some(env => env.id === option.id) ? 'from-blue-400/10 to-indigo-400/10' : 'from-gray-200/5 to-gray-100/5'}`}></div>
                    <div className="relative z-10">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className={`font-semibold ${workEnvironment.some(env => env.id === option.id) ? 'text-blue-700' : 'text-gray-800'}`}>{option.name}</h3>
                        {workEnvironment.some(env => env.id === option.id) && (
                          <span className="text-blue-600 bg-blue-100/70 rounded-full w-6 h-6 flex items-center justify-center">✓</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{option.description}</p>
                      <div className="mt-2 text-xs text-gray-600 bg-white/50 px-2 py-1 rounded-md">
                        <strong>Pros:</strong> {option.pros.slice(0, 2).join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">Choose Your Market Scope</h2>
              <p className="text-gray-700 mb-6">
                Select the geographical reach you want for your business idea.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {MARKET_SCOPE_OPTIONS.map((option) => (
                  <div 
                    key={option.id}
                    onClick={() => {
                      console.log('Market Scope Selected:', option.id);
                      setMarketScope(option.id);
                      track('set_market_scope', { id: option.id, name: option.name });
                    }}
                    className={`
                      relative overflow-hidden rounded-xl shadow-sm border p-4 text-left transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow cursor-pointer
                      ${marketScope === option.id
                        ? 'bg-blue-50/70 backdrop-blur-sm border-blue-300/50 ring-2 ring-blue-500/70' 
                        : 'bg-white/30 backdrop-blur-sm border-white/40 hover:bg-blue-50/50'
                      }
                    `}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${marketScope === option.id ? 'from-blue-400/10 to-indigo-400/10' : 'from-gray-200/5 to-gray-100/5'}`}></div>
                    <div className="relative z-10">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className={`font-semibold ${marketScope === option.id ? 'text-blue-700' : 'text-gray-800'}`}>{option.name}</h3>
                        {marketScope === option.id && (
                          <span className="text-blue-600 bg-blue-100/70 rounded-full w-6 h-6 flex items-center justify-center">✓</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{option.description}</p>
                      <div className="mt-2 text-xs text-gray-600 bg-white/50 px-2 py-1 rounded-md">
                        <strong>Pros:</strong> {option.pros.join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4">Review & Generate</h2>
            <p className="text-gray-700 mb-6">
              Review your selections before generating your business ideas.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-sm p-4">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/5 to-indigo-400/5"></div>
                <div className="relative z-10">
                  <h3 className="font-medium text-lg text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-3">Your Skills</h3>
                  {selectedSkills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {selectedSkills.map((skill) => (
                        <span 
                          key={skill.id}
                          className="bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center shadow-sm text-gray-800 border border-white/50"
                        >
                          {skill.name} 
                          <span className="text-blue-600 text-sm ml-1 font-medium">({skill.level})</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-red-500 bg-white/50 backdrop-blur-sm p-2 rounded-lg">No skills selected! Please go back and select at least one skill.</p>
                  )}
                </div>
              </div>

              <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-sm p-4">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400/5 to-blue-400/5"></div>
                <div className="relative z-10">
                  <h3 className="font-medium text-lg text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 mb-3">Time Commitment</h3>
                  {timeCommitment ? (
                    <div className="bg-white/70 backdrop-blur-sm p-2 rounded-lg border border-white/50">
                      <p className="font-medium text-gray-700">{timeCommitment.name}</p>
                      <p className="text-sm text-gray-600 mt-1">{timeCommitment.description}</p>
                    </div>
                  ) : (
                    <p className="text-red-500 bg-white/50 backdrop-blur-sm p-2 rounded-lg">No time commitment selected! Please go back and make a selection.</p>
                  )}
                </div>
              </div>

              <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-sm p-4">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-400/5 to-purple-400/5"></div>
                <div className="relative z-10">
                  <h3 className="font-medium text-lg text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-3">Work Environment</h3>
                  {workEnvironment.length > 0 ? (
                    <div className="space-y-2 bg-white/70 backdrop-blur-sm p-2 rounded-lg border border-white/50">
                      {workEnvironment.map(env => (
                        <div key={env.id} className="text-gray-700">
                          <span className="font-medium">{env.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-red-500 bg-white/50 backdrop-blur-sm p-2 rounded-lg">No work environment selected! Please go back and make a selection.</p>
                  )}
                </div>
              </div>

              <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-sm p-4">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/5 to-purple-400/5"></div>
                <div className="relative z-10">
                  <h3 className="font-medium text-lg text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-3">Market Scope</h3>
                  {marketScope ? (
                    <div className="bg-white/70 backdrop-blur-sm p-2 rounded-lg border border-white/50">
                      <p className="font-medium text-gray-700">{MARKET_SCOPE_OPTIONS.find(option => option.id === marketScope)?.name || 'Not selected'}</p>
                      <p className="text-sm text-gray-600 mt-1">{MARKET_SCOPE_OPTIONS.find(option => option.id === marketScope)?.description || ''}</p>
                    </div>
                  ) : (
                    <p className="text-red-500 bg-white/50 backdrop-blur-sm p-2 rounded-lg">No market scope selected! Please go back and make a selection.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-6">
              <button
                onClick={() => setStep(4)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Back
              </button>
              <button
                onClick={handleComplete}
                className={`
                  px-4 py-2 rounded-md text-white text-sm font-medium
                  ${marketScope ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}
                `}
                disabled={!marketScope}
              >
                Generate Ideas
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Navigation handlers
  const handleNextStep = () => {
    console.log('Current Step:', step);
    console.log('Current State:', {
      selectedSkills,
      timeCommitment,
      workEnvironment,
      marketScope
    });

    if (step === 1 && selectedSkills.length === 0) {
      console.error('No skills selected');
      alert('Please select at least one skill');
      return;
    }
    if (step === 2 && !timeCommitment) {
      console.error('No time commitment selected');
      alert('Please select a time commitment');
      return;
    }
    if (step === 3 && workEnvironment.length === 0) {
      console.error('No work environment selected');
      alert('Please select at least one work environment');
      return;
    }
    if (step === 4 && !marketScope) {
      console.error('No market scope selected');
      alert('Please select a market scope');
      return;
    }
    
    // If we're on the last step, call onComplete
    if (step === 5) {
      console.log('Attempting to complete wizard');
      handleComplete();
      return;
    }
    
    const next = Math.min(step + 1, 5);
    track('wizard_next', { from_step: step, to_step: next });
    setStep(next);
  };

  const handleComplete = () => {
    console.log('Attempting to complete wizard with:', {
      selectedSkills,
      timeCommitment,
      workEnvironment,
      marketScope
    });

    if (selectedSkills.length === 0) {
      console.error('At least one skill must be selected');
      alert('Please select at least one skill');
      return;
    }

    if (!timeCommitment) {
      console.error('Time commitment is required');
      alert('Please select a time commitment');
      return;
    }

    if (workEnvironment.length === 0) {
      console.error('At least one work environment must be selected');
      alert('Please select at least one work environment');
      return;
    }

    if (!marketScope) {
      console.error('Market scope is required');
      alert('Please select a market scope');
      return;
    }

    // Validate market scope is one of the allowed values
    const validMarketScopes: MarketScope[] = ['local', 'national', 'international'];
    if (!validMarketScopes.includes(marketScope)) {
      console.error('Invalid market scope:', marketScope);
      alert('Please select a valid market scope');
      return;
    }

    console.log('Wizard completion validated. Calling onComplete.');
    onComplete({
      skills: selectedSkills,
      timeCommitment,
      workEnvironment,
      marketScope
    });
  };

  const handlePreviousStep = () => {
    const prevStep = Math.max(step - 1, 1);
    track('wizard_previous', { from_step: step, to_step: prevStep });
    setStep(prevStep);
  };

  return (
    <div className="flex flex-col">
      <div className="container mx-auto px-4 py-8">
        <div className="relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg border border-white/30 shadow-lg p-6">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 opacity-5"></div>
          <div className="relative z-10">
            {/* Step indicator */}
            <div className="mb-6 flex justify-center">
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((stepNumber) => (
                  <div 
                    key={stepNumber}
                    className={`w-3 h-3 rounded-full ${step >= stepNumber ? 'bg-blue-500' : 'bg-gray-300'} transition-colors`}
                  />
                ))}
              </div>
            </div>
            
            {renderStepContent()}
            
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <button 
                  onClick={handlePreviousStep}
                  className="px-4 py-2 bg-white/60 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-white transition-colors border border-gray-200/50 shadow-sm hover:shadow transition-all duration-300"
                >
                  Previous
                </button>
              )}
              
              {onCancel && (
                <button 
                  onClick={onCancel}
                  className="px-4 py-2 bg-red-100/70 backdrop-blur-sm text-red-600 rounded-lg hover:bg-red-200/80 ml-auto mr-4 shadow-sm hover:shadow transition-all duration-300 border border-red-200/50"
                >
                  Cancel
                </button>
              )}
              
              {step < 5 ? (
                <button 
                  onClick={handleNextStep}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Next
                </button>
              ) : (
                <button 
                  onClick={() => {
                    track('click_generate_from_review', {
                      skills_count: selectedSkills.length,
                      has_time: !!timeCommitment,
                      work_environment_count: workEnvironment.length,
                      market_scope: marketScope
                    });
                    handleComplete();
                  }}
                  className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-lg hover:shadow-green-500/30 transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Complete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdeaGeneratorWizard;
